<template>
  <div
    v-if="isImpersonating"
    class="impersonation-banner"
  >
    <div class="container mx-auto px-4 py-3">
      <div class="flex flex-col sm:flex-row items-center justify-between gap-2">
        <div class="flex items-center space-x-2 text-sm">
          <div class="impersonation-icon">
            <UserCircleIcon class="h-5 w-5" />
          </div>
          <span class="opacity-90">Viewing as:</span>
          <span class="font-medium">
            {{ impersonatedUser?.email || 'user' }}
          </span>
        </div>
        <button
          class="return-button"
          @click="stopImpersonation"
        >
          <ArrowLeftOnRectangleIcon class="h-4 w-4" />
          <span>Return to Admin</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import api from '@/api/axios';
import {
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
} from '@heroicons/vue/24/solid';

export default {
  name: 'ImpersonationBanner',
  components: {
    UserCircleIcon,
    ArrowLeftOnRectangleIcon,
  },
  setup() {
    const isImpersonating = ref(false);
    const impersonatedUser = ref(null);
    const impersonator = ref(null);
    let pollingInterval = null;

    const checkImpersonationStatus = async () => {
      try {
        const response = await api.get('/api/impersonate/status/');
        const wasImpersonating = isImpersonating.value;
        isImpersonating.value = response.data.is_impersonating;
        
        if (response.data.is_impersonating) {
          impersonatedUser.value = response.data.impersonated_user;
          impersonator.value = response.data.impersonator;
          
          // Start polling if we weren't already polling
          if (!wasImpersonating && !pollingInterval) {
            startPolling();
          }
        } else {
          // Stop polling if we're not impersonating
          if (wasImpersonating && pollingInterval) {
            stopPolling();
          }
          impersonatedUser.value = null;
          impersonator.value = null;
        }
      } catch (error) {
        console.error('Error checking impersonation status:', error);
      }
    };

    const startPolling = () => {
      // Only start polling if we don't already have an interval
      if (!pollingInterval) {
        pollingInterval = setInterval(checkImpersonationStatus, 30000); // Every 30 seconds
      }
    };

    const stopPolling = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
    };

    const stopImpersonation = async () => {
      try {
        const response = await api.post('/api/impersonate/stop/');
        
        if (response.data.success) {
          // Clear only client-specific data, NOT the auth token
          // The backend session handles the user switching
          localStorage.removeItem('accountId');
          localStorage.removeItem('portalToken');
          
          // Clear session storage except for auth-related items
          const token = localStorage.getItem('token');
          sessionStorage.clear();
          if (token) {
            localStorage.setItem('token', token);
          }
          
          // Force a full page reload to refresh all state
          window.location.href = response.data.redirect_url || '/admin';
        }
      } catch (error) {
        console.error('Error stopping impersonation:', error);
        alert('Failed to stop impersonation. Please try again.');
      }
    };

    onMounted(() => {
      // Check status once on mount
      checkImpersonationStatus();
    });

    onUnmounted(() => {
      // Properly cleanup polling interval when component unmounts
      stopPolling();
    });

    return {
      isImpersonating,
      impersonatedUser,
      impersonator,
      stopImpersonation,
    };
  },
};
</script>

<style scoped>
.impersonation-banner {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: rgba(251, 191, 36, 0.15); /* Subtle amber background */
  border-bottom: 1px solid rgba(251, 191, 36, 0.3);
  backdrop-filter: blur(10px);
  color: rgb(245, 158, 11); /* Amber text */
}

/* Dark mode styles - more subtle */
@media (prefers-color-scheme: dark) {
  .impersonation-banner {
    background: rgba(251, 191, 36, 0.08); /* Much more subtle in dark mode */
    border-bottom: 1px solid rgba(251, 191, 36, 0.2);
    color: rgba(251, 191, 36, 0.9);
  }
}

/* Also check for DaisyUI dark theme */
[data-theme="dark"] .impersonation-banner {
  background: rgba(251, 191, 36, 0.08);
  border-bottom: 1px solid rgba(251, 191, 36, 0.2);
  color: rgba(251, 191, 36, 0.9);
}

.impersonation-icon {
  display: inline-flex;
  padding: 0.25rem;
  background: rgba(251, 191, 36, 0.2);
  border-radius: 0.375rem;
}

.return-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgb(245, 158, 11);
  color: white;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.return-button:hover {
  background: rgb(217, 119, 6);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.return-button:active {
  transform: translateY(0);
}

/* Make button even more prominent in dark mode */
[data-theme="dark"] .return-button {
  background: rgb(251, 191, 36);
  color: rgb(0, 0, 0);
  border: 2px solid rgb(251, 191, 36);
}

[data-theme="dark"] .return-button:hover {
  background: rgb(245, 158, 11);
  border-color: rgb(245, 158, 11);
  box-shadow: 0 4px 20px rgba(251, 191, 36, 0.4);
}
</style>